import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedNumber } from 'react-intl';
import { observer } from 'mobx-react-lite';

import useForm from '../hooks/useForm';
import useCalculatorStepState from '../hooks/useCalculatorStepState';
import ElementAttributes from '../definitions/ElementAttributes';
import withFieldset from '../functions/withFieldset';
import scrollToCalculatorStep from '../functions/scrollToCalculatorStep';
import Sizes from '../definitions/Sizes';
import MessagePropType from '../definitions/MessagePropType';
import useCalculator from '../hooks/useCalculator';
import PrimaryButton from './PrimaryButton';
import { SIZES as PARAGRAPH_SIZES } from './Paragraph';
import AccordionCalculation from './AccordionCalculation';


function CalculatorStepAccordion({
    value,
    valueFormat,

    title,
    ctaText,
    ctaDataName,

    stepNumber,
    showBadge,
    messageBannerProps,
    shouldAlwaysShowMessageBanner,

    hasCompleteButton,
    onCheckComplete,
    onConfirmComplete,

    isInitiallyExpanded,
    onToggleExpand,

    canBeDisabled,
    accordionDataName,

    children,
    ...otherProps
}) {
    const form = useForm();
    const calculator = useCalculator();
    const [ isExpanded, setIsExpanded ] = useState(isInitiallyExpanded);

    // We don't want this to update with each render
    const handlePreviousStepConfirmComplete = useCallback(() =>
        // Requirement: Open accordion once user has clicked confirm for the previous step
        setIsExpanded(true)
    , [ setIsExpanded ]);

    const calculatorStepState = useCalculatorStepState({
        number: stepNumber,
        hasCompleteButton,
        onCheckComplete,
        onPreviousStepConfirmComplete: handlePreviousStepConfirmComplete,
    });

    // Due to the possibility of React rendering steps out of order,
    // We can't safely throw if the previous step isn't found
    const isDisabled = canBeDisabled
        ? !calculator.hasCompletedPreviousStep(stepNumber, false)
        : false;


    function handleToggleExpand() {
        setIsExpanded(value => !value);
        scrollToCalculatorStep(stepNumber);

        onToggleExpand?.(stepNumber);
    }

    // if previous step becomes incomplete, then we want to close accordion
    useEffect(() => {
        if (!calculatorStepState?.hasCompletedPreviousStep) {
            setIsExpanded(false);
        }
    }, [ calculatorStepState?.hasCompletedPreviousStep ]);

    function handleClickComplete() {
        if (!calculatorStepState.canConfirmComplete) {
            return; // do nothing; if there are validation errors, they will be shown.
        }

        calculatorStepState.confirmComplete();

        // Requirement: Collapse completed accordion and scroll to top of
        //  completed accordion to prevent scrolling issues with height change
        setIsExpanded(false);
        scrollToCalculatorStep(stepNumber);

        onConfirmComplete?.(stepNumber, form.values);
    }

    const dataNameProps = { 'data-name': accordionDataName };

    return (
        <CalculatorStepAccordion.Container
            {...{ [ElementAttributes.CALCULATOR_STEP_KEY]: stepNumber }}
            {...otherProps}
        >
            <AccordionCalculation
                title={title}
                subtitle={
                    (calculatorStepState.isComplete && !isExpanded)
                        ? getSubtitle(form, value, valueFormat)
                        : undefined
                }

                messageBannerProps={messageBannerProps}
                shouldAlwaysShowMessageBanner={shouldAlwaysShowMessageBanner}

                badge={showBadge ? stepNumber : undefined}
                disabled={isDisabled}

                isInitiallyExpanded={isInitiallyExpanded}
                isExpanded={isExpanded}
                onClick={handleToggleExpand}
                {...dataNameProps}
            >
                {typeof children === 'function'
                    ? children({
                        confirmComplete: handleClickComplete,
                    })
                    : children
                }

                <If condition={hasCompleteButton}>
                    <PrimaryButton
                        className="nextStep"
                        data-name={ctaDataName}
                        disabled={!calculatorStepState.canConfirmComplete}
                        aria-disabled={!calculatorStepState.canConfirmComplete}
                        onClick={handleClickComplete}
                        message={ctaText}
                        type="button"
                    />
                </If>
            </AccordionCalculation>
        </CalculatorStepAccordion.Container>
    );
}

CalculatorStepAccordion.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    valueFormat: PropTypes.string,

    title: MessagePropType.isRequired,
    ctaText: MessagePropType,
    ctaDataName: PropTypes.string,

    stepNumber: PropTypes.number.isRequired,
    showBadge: PropTypes.bool,
    messageBannerProps: PropTypes.shape({
        variant: PropTypes.string.isRequired,
        iconKey: PropTypes.string,
        iconSize: PropTypes.oneOf([
            Sizes.ICONS.S,
            Sizes.ICONS.L,
        ]),
        message: MessagePropType.isRequired,
        messageSize: PropTypes.oneOf(
            Object.values(PARAGRAPH_SIZES),
        ),
    }),
    shouldAlwaysShowMessageBanner: PropTypes.bool,

    hasCompleteButton: PropTypes.bool,
    onCheckComplete: PropTypes.func,
    onConfirmComplete: PropTypes.func,

    isInitiallyExpanded: PropTypes.bool,
    onToggleExpand: PropTypes.func,

    canBeDisabled: PropTypes.bool,
    accordionDataName: PropTypes.string,

    children: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.elementType, // render prop
    ]).isRequired,
};

CalculatorStepAccordion.defaultProps = {
    value: undefined,
    valueFormat: undefined,

    ctaText: undefined,
    ctaDataName: undefined,

    showBadge: false,
    messageBannerProps: undefined,
    shouldAlwaysShowMessageBanner: false,

    hasCompleteButton: undefined,
    onCheckComplete: undefined,
    onConfirmComplete: undefined,

    isInitiallyExpanded: undefined,
    onToggleExpand: undefined,

    canBeDisabled: undefined,
    accordionDataName: undefined,
};

// eslint-disable-next-line react/no-multi-comp
function getSubtitle(form, value, format) {
    if (form == null || value == null || format == null) {
        return undefined;
    }

    if (!form.getValue(value)) {
        return undefined;
    }

    return (
        <FormattedNumber
            value={typeof value === 'string' ? form.getValue(value) : value}
            format={format}
        />
    );
}

CalculatorStepAccordion.Container = styled.section`
    display: flex;
    flex-direction: column;

    position: relative;

    .nextStep {
        display: block;
        max-width: 350px;
        margin: ${Sizes.SPACING.TWO} auto ${Sizes.SPACING.ONE_AND_A_HALF};
    }
`;

export default withFieldset(observer(CalculatorStepAccordion));
